/**
 * Feature switches — read at RENDER time, not import time, so a test (or the
 * runtime config.js the container rewrites) can flip one without a rebuild.
 *
 * Precedence, same as authConfig: `window.__APP_CONFIG__.features` → build-time
 * `REACT_APP_FEATURE_*` → the default here.
 */
import { nonProdHost } from "./namedDrafts";

export type FeaturesConfig = {
  /** "Duplicate A Past Job" on Step 1 + its modal. Hidden 2026-09-10 (Jack: "should
   *  hide/remove the duplicate feature for now"). Nothing behind it is removed. */
  duplicateJob?: boolean;
  /** Save as draft · Drafts mode · Copy preview link. ON everywhere from 2026-09-23;
   *  `false` here turns it off for one environment without a build (services/namedDrafts.ts). */
  namedDrafts?: boolean;
  /** Step 4's Disclaimer builder (#364). Dev/QA only until Jack ships it (2026-10-15);
   *  `true` forces it on for one environment, `false` off (services/features.ts). */
  disclaimerBuilder?: boolean;
  /** Milestone 2 phase 1: the left block palette + guardrails-off test bed. Off in
   *  production unless forced; `?m2=1` on a non-prod host (services/m2Blocks.ts). */
  m2Blocks?: boolean;
};

/* `window.__APP_CONFIG__` is declared once, in authConfig.ts (AppConfig carries `features`) */
function runtime(): FeaturesConfig {
  const cfg = typeof window !== "undefined" ? window.__APP_CONFIG__ : undefined;
  return (cfg && cfg.features) || {};
}

/**
 * ── THE DISCLAIMER BUILDER IS DEV / QA ONLY (2026-10-15, Jack) ───────────────────────
 * "Merge them behind the same dev/QA-only switch the hero overlay uses. Prod users never
 * see them." So the default is the overlay editor's own host test (`nonProdHost`): on on
 * -dev / -qa / -uat / localhost / file://, off on emailbuilder.lennar.com. Off means
 * production behaves exactly as before #364: Step 4 reads "Branding & Details", no
 * Disclaimer section, the footer is the template's own, and no Disclaimer Text /
 * Disclaimer Builder Detail keys are sent. `features.disclaimerBuilder` in config.js
 * (or REACT_APP_FEATURE_DISCLAIMER_BUILDER) overrides either way.
 */
export function disclaimerBuilderEnabled(loc?: { hostname: string; protocol: string }): boolean {
  const o = runtime().disclaimerBuilder;
  if (typeof o === "boolean") return o;
  if (process.env.REACT_APP_FEATURE_DISCLAIMER_BUILDER === "true") return true;
  return nonProdHost(loc);
}

/** the Duplicate-a-past-job entry point (button + modal). Default OFF. */
export function dupJobEnabled(): boolean {
  const o = runtime().duplicateJob;
  if (typeof o === "boolean") return o;
  return process.env.REACT_APP_FEATURE_DUP_JOB === "true";
}
